import type {
    AssetTranscript,
    LanguageEnum,
    SceneRecommendation,
    VideoPlan,
    VoiceoverAssetResponse,
} from 'js/src/client';
import {
    ApiService,
    AsyncOperationNameEnum,
    NarrativeThreadEnum,
    TaskResultMessageTypeEnum,
} from 'js/src/client';
import type { VideoPlanWithCreationInfo } from 'js/src/data/video-planner/video-planner-types';
import { ImmutableFromJS } from 'js/src/libs/consts';
import WebSocketClient from 'js/src/libs/websocket-client';
import { createBestGuessTranscript } from 'js/src/luminary';

export const generateVoiceoverAsset = async ({
    text,
    voice,
    languageCode,
}: {
    text: string;
    voice: string;
    languageCode: string;
}) => {
    const response = await ApiService.textToVoiceoverAssetCreate({
        body: {
            text,
            voice_preset: voice,
            language_code: languageCode,
        },
    });
    const { async_operation_uuid } = response.data;
    const ws = WebSocketClient.getInstance();
    const result = await ws.waitForMessage<VoiceoverAssetResponse>(
        async_operation_uuid,
        TaskResultMessageTypeEnum.TASK_RESULT,
        AsyncOperationNameEnum.CONVERT_TEXT_TO_VOICEOVER_ASSET,
    );
    return {
        voiceoverAsset: ImmutableFromJS(result.audio_asset),
        transcript: {
            transcriptWords: result.transcript,
            language: result.language,
        } as AssetTranscript,
    };
};

export const createRecommendationAIVoiceover = async ({
    sceneRecommendations,
    videoPlan,
}: {
    sceneRecommendations: SceneRecommendation[];
    videoPlan: VideoPlan;
}) => {
    const voiceId = videoPlan.ai_voice;
    if (!voiceId) {
        throw new Error('No AI voice selected for video plan');
    }
    const startTime = Date.now();
    const { sentences } = createBestGuessTranscript(sceneRecommendations);
    const scriptText = sentences.join(' ');
    const language = sceneRecommendations[0].scene_data.language as LanguageEnum;
    const { voiceoverAsset, transcript } = await generateVoiceoverAsset({
        text: scriptText,
        voice: voiceId,
        languageCode: language,
    });
    const endTime = Date.now();
    analytics.track('Voiceover Created for Video Plan', {
        duration: endTime - startTime,
        voiceoverDurationSeconds: voiceoverAsset.get('duration'),
        voiceId,
    });
    return { voiceoverAsset, transcript };
};

export function isVideoPlanFullyLoaded(videoPlan: VideoPlanWithCreationInfo): boolean {
    /**
     * Returns a boolean indicating whether the video plan is ready to be
     * saved or edited.
     * Ready means the full video has been generated including AI VO.
     */
    const hasPartiallyLoaded = videoPlan.scene_recommendations !== null;
    const hasFullyLoadedRecommendations =
        hasPartiallyLoaded && videoPlan.scene_recommendations!.every((scene) => scene !== null);
    const voiceover = videoPlan.voiceover.asset;
    const hasVoiceoverIfRequired =
        voiceover !== null || videoPlan.narrative_thread === NarrativeThreadEnum.TEXT;
    const isReady = hasFullyLoadedRecommendations && hasVoiceoverIfRequired;
    return isReady;
}
